import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Upload, UploadOnStatusChangeEvent } from '@progress/kendo-react-upload';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { ChangeEvent } from 'react';
import { useVoting } from '../../../../contexts/VotingContext';
import { buildAuthQueryString } from '../../VoteUtils';
import { IBallotNominationView, IVoterNominationView, IApiResponseModel } from '../../../../types/voteTypes';
import { InfoBanner } from '../../../common/InfoBanner';
import { LoadingButton } from '../../../common/LoadingButton';
import { Editor, EditorChangeEvent, EditorTools } from '@progress/kendo-react-editor';
import dompurify from 'dompurify';

const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    ForeColor,
    BackColor,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    OrderedList,
    UnorderedList,
    FontSize,
    FontName,
    Link,
    Unlink,
} = EditorTools;

interface NominationProps {
    config: IBallotNominationView,
    nomination: IVoterNominationView,
    onChange: (nomination: IVoterNominationView) => void,
    setNominationError: (message: string) => void
    onBack: (() => void) | null,
    onSubmitted: (nominationName: string) => void
    refresh: (() => void),
}

export const Nomination: React.FC<NominationProps> = ({ config, nomination, onChange, setNominationError, onBack, onSubmitted, refresh }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setSubmitting] = useState(false)
    const [isAccepting, setAccepting] = useState(false)
    const [showPhotoMissingError, setShowPhotoMissingError] = useState<boolean>(false)
    const [acceptanceId, setAcceptanceId] = useState<string | null>(null);
    const [image, setImage] = useState<File | undefined>(undefined);
    const { voter, ballot, previewId, isAdmin, isPreview } = useVoting();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        setAcceptanceId(urlParams.get('acceptanceId'));
    }, []);

    if (!voter || !ballot) return null;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        if (id === "nomineeName") {
            onChange({ ...nomination, nomineeName: value });
        } else if (id === "nomineePosition") {
            onChange({ ...nomination, nomineePosition: value });
        } else if (id === "nomineePhoneMobile") {
            onChange({ ...nomination, nomineePhoneMobile: value });
        } else if (id === "nomineePhoneWork") {
            onChange({ ...nomination, nomineePhoneWork: value });
        } else if (id === "nomineeEmail") {
            onChange({ ...nomination, nomineeEmail: value });
        } else if (id === "nomineeCategory") {
            onChange({ ...nomination, nomineeCategory: value });
        } else if (id === "nomineeGroup") {
            onChange({ ...nomination, nomineeGroup: value });
        } else if (id === "seconderName") {
            onChange({ ...nomination, seconderName: value });
        } else if (id === "seconderEmail") {
            onChange({ ...nomination, seconderEmail: value });
        }
    };

    const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const { id, value } = event.target;
        if (id === "nomineeAddress") {
            onChange({ ...nomination, nomineeAddress: value });
        } else if (id === "custom1") {
            onChange({ ...nomination, custom1: value });
        } else if (id === "custom2") {
            onChange({ ...nomination, custom2: value });
        }
    };

    const handleEditorChange = (event: EditorChangeEvent) => {
        onChange({ ...nomination, nomineeBio: event.html });
    };

    const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const { id, value } = event.target;
        if (id === "nomineeCategory") {
            onChange({ ...nomination, nomineeCategory: value });
        } else if (id === "nomineeGroup") {
            onChange({ ...nomination, nomineeGroup: value });
        }
    };

    const handleSubmit = () => {
        if (!voter || !ballot) return null;
        if (!formRef.current?.checkValidity() || photoMissing) {
            formRef.current?.classList.add('was-validated');
            setShowPhotoMissingError(photoMissing);
            setSubmitting(false);
            return;
        }

        // Reset photo missing
        setShowPhotoMissingError(false);

        setSubmitting(true);
        formRef.current?.classList.remove('was-validated');

        const formData = new FormData();
        formData.append('nomination', JSON.stringify(nomination));
        if (image) {
            formData.append('image', image);
        }

        axios.post<IApiResponseModel>(`/vote/submitNomination?${buildAuthQueryString(voter, ballot, previewId ?? null)}`, formData)
            .then((response) => {
                if (response.data.isSuccessful) {
                    onSubmitted(nomination.nomineeName);
                }
                else {
                    setNominationError(response.data.errorMessage)
                }
                setSubmitting(false);
            })
            .catch(function (error) {
                alert(error)
            })
    };

    const handleAccept = () => {
        if (!voter || !ballot) return null;

        setAccepting(true);

        axios.post<IApiResponseModel>('/vote/acceptNomination', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            personalId: voter.personalId,
            acceptanceId,
            previewId,
            nominationId: nomination.nominationId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    refresh();
                }
                else {
                    setNominationError(response.data.errorMessage)
                }
                setAccepting(false);
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handleDecline = () => {
        if (!voter || !ballot) return null;

        setAccepting(true);

        axios.post<IApiResponseModel>('/vote/declineNomination', {
            id: voter.uniqueId,
            key: voter.guid,
            ballotId: ballot.id,
            personalId: voter.personalId,
            acceptanceId,
            previewId,
            nominationId: nomination.nominationId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    refresh();
                }
                else {
                    setNominationError(response.data.errorMessage)
                }
                setAccepting(false);
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handlePhotoStatusChange = (event: UploadOnStatusChangeEvent) => {
        const photoId = event.response?.response?.photoId;
        if (photoId) {
            onChange({ ...nomination, nomineePhotoId: photoId });
        }
    };

    const handlePhotoAdd = (event: any) => {
        const file = event.affectedFiles[0].getRawFile();
        setImage(file);
    }

    // Work out if we are readonlyi
    let readOnly: boolean = false;

    // Admin link
    if (isAdmin) {
        readOnly = false;
    }
    // Not an admin but is preview/ballot viewer link
    else if (previewId) {
        // Null nominationId means we are preview and a nomination ID means we are a ballot viewer
        readOnly = nomination.nominationId != null;
    }
    // Nominee link
    else if (acceptanceId) {
        // Read only if not accepted yet
        readOnly = !nomination.isAccepted;
    }
    // Nominator link
    else {
        // Nomination has been accepted and only editable by nominee now
        readOnly = nomination.isAccepted;
    }

    //const readOnly: boolean = (nomination.isAccepted && acceptanceId) ||
    //    !!acceptanceId && !nomination.isAccepted;
    const photoMissing: boolean = config.allowPhoto && config.isPhotoRequired &&
        !(nomination.nomineePhotoId || image);

    // Modify upload control text
    loadMessages(
        {
            upload: {
                dropFilesHere: 'Drop file here to upload',
                select: 'Select File'
            }
        },
        'photoUploadMessages'
    );

    const getNominationImage = () => {
        // Prioritise local upload first incase overwriting
        if (image) {
            return <img src={URL.createObjectURL(image)} alt="alt" className="max-w-64 max-h-64 mb-2" />;
        }
        // Retreive from S3 if there is an image
        else if (nomination.nomineePhotoId) {
            let url = `/vote/nominationPhoto?${buildAuthQueryString(voter, ballot, previewId ?? null)}&nominationId=${nomination.nominationId}&nominationPhotoId=${nomination.nomineePhotoId}`;
            return <img src={url} alt="alt" className="max-w-64 max-h-64 mb-2" />;
        }

        return null;
    }

    const getNominationBanner = () => {
        // Admin link
        if (isPreview && nomination.nominationId) {
            return <>
                {!nomination.isAccepted &&
                    <InfoBanner isSticky={true}>This nomination has not been accepted.</InfoBanner>
                }
                {nomination.isAccepted &&
                    <InfoBanner isSticky={true}>This nomination has been accepted.</InfoBanner>
                }
            </>;
        }
        else if (isAdmin) {
            return <>
                {!nomination.isAccepted &&
                    <LoadingButton className="btn btn-green"
                        onBtnClick={handleAccept}
                        btnText="Accept Nomination"
                        isLoading={isAccepting}
                        confirmText="Are you sure you want to accept this nomination?" />
                }
                {nomination.isAccepted &&
                    <LoadingButton className="btn btn-red"
                        onBtnClick={handleDecline}
                        btnText="Decline Nomination"
                        isLoading={isAccepting}
                        confirmText="Are you sure you want to decline this nomination that has been accepted?" />
                }
            </>;
        }
        // Nominee link
        else if (acceptanceId && !nomination.isAccepted) {
            return <InfoBanner isSticky={true}>
                <>
                    <div className="mb-2">Please review your nomination below and press the Accept Nomination button to confirm your acceptance. If you do not wish to accept, you may now close the page.</div>
                    <div className="mb-4 italic">Once accepted you may edit the nomination form if required.</div>
                    <LoadingButton className="btn btn-green"
                        onBtnClick={handleAccept}
                        btnText="Accept Nomination"
                        isLoading={isAccepting}
                        confirmText="Are you sure you want to accept this nomination?" />
                </>
            </InfoBanner>;
        }
        // Voter link
        else if (!acceptanceId && nomination.isAccepted) {
            return <InfoBanner isSticky={true}>This nomination has been accepted and may now only be edited by the nominee.</InfoBanner>;
        }

        return null;
    }

    return <>
        {getNominationBanner()}

        <form ref={formRef}>
            <div className="mb-4">
                <label htmlFor="nomineeName" className="block font-medium mb-2">Name</label>
                <input id="nomineeName" type="text"
                    className="max-w-none mb-1"
                    required
                    onChange={handleChange}
                    value={nomination.nomineeName ?? ''}
                    readOnly={readOnly}
                />
                <span className="field-error">Name is required.</span>
            </div>

            {config.allowEmail &&
                <div className="mb-4">
                    <label htmlFor="nomineeEmail" className="block font-medium mb-2">Email</label>
                    <input id="nomineeEmail" type="email"
                        className="max-w-none mb-1"
                        required={config.isEmailRequired}
                        onChange={handleChange}
                        value={nomination.nomineeEmail ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Email is required.</span>
                </div>
            }

            {config.allowSeconderName &&
                <div className="mb-4">
                    <label htmlFor="seconderName" className="block font-medium mb-2">{config.seconderNameLabel}</label>
                    <input id="seconderName" type="text"
                        className="max-w-none mb-1"
                        required={config.isSeconderNameRequired}
                        onChange={handleChange}
                        value={nomination.seconderName ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Seconder name is required.</span>
                </div>
            }

            {config.allowSeconderEmail &&
                <div className="mb-4">
                    <label htmlFor="seconderEmail" className="block font-medium mb-2">{config.seconderEmailLabel}</label>
                    <input id="seconderEmail" type="email"
                        className="max-w-none mb-1"
                        required={config.isSeconderEmailRequired}
                        onChange={handleChange}
                        value={nomination.seconderEmail ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Seconder email is required.</span>
                </div>
            }

            {config.allowCategory &&
                <div className="mb-4">
                    <label htmlFor="nomineeCategory" className="block font-medium mb-2">{config.categoryLabel}</label>
                    {config.isCategoryOptions
                        ? <select id="nomineeCategory" className="max-w-none mb-1"
                            required={config.isCategoryRequired}
                            onChange={handleSelectChange}
                            value={nomination.nomineeCategory ?? ''}
                            disabled={readOnly}>
                            <option value="">-- Select --</option>
                            {config.categoryOptions.split("\r\n").map((o: string) => o.trim()).filter((o): o is string => o.length > 0).map((o: string) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                        : <input id="nomineeCategory" type="text"
                            className="max-w-none mb-1"
                            required={config.isCategoryRequired}
                            onChange={handleChange}
                            value={nomination.nomineeCategory ?? ''}
                            readOnly={readOnly}
                        />
                    }
                    <span className="field-error">{config.categoryLabel} is required.</span>
                </div>
            }

            {config.allowGroup &&
                <div className="mb-4">
                    <label htmlFor="nomineeGroup" className="block font-medium mb-2">{config.groupLabel}</label>
                    {config.isGroupOptions
                        ? <select id="nomineeGroup" className="max-w-none mb-1"
                            required={config.isGroupRequired}
                            onChange={handleSelectChange}
                            value={nomination.nomineeGroup ?? ''}
                            disabled={readOnly}>
                            <option value="">-- Select --</option>
                            {config.groupOptions.split("\r\n").map((o: string) => o.trim()).filter((o): o is string => o.length > 0).map((o: string) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                        : <input id="nomineeGroup" type="text"
                            className="max-w-none mb-1"
                            required={config.isGroupRequired}
                            onChange={handleChange}
                            value={nomination.nomineeGroup ?? ''}
                            readOnly={readOnly}
                        />
                    }
                    <span className="field-error">{config.groupLabel} is required.</span>
                </div>
            }

            {config.allowPosition &&
                <div className="mb-4">
                    <label htmlFor="nomineePosition" className="block font-medium mb-2">Position</label>
                    <input id="nomineePosition" type="text"
                        className="max-w-none mb-1"
                        required={config.isPositionRequired}
                        onChange={handleChange}
                        value={nomination.nomineePosition ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Position is required.</span>
                </div>
            }

            {config.allowAddress &&
                <div className="mb-4">
                    <label htmlFor="nomineeAddress" className="block font-medium mb-2">Address</label>
                    <textarea id="nomineeAddress"
                        className="max-w-none mb-1"
                        required={config.isAddressRequired}
                        onChange={handleTextAreaChange}
                        value={nomination.nomineeAddress ?? ''}
                        readOnly={readOnly}
                        rows={3}
                    />
                    <span className="field-error">Address is required.</span>
                </div>
            }

            {config.allowPhoneWork &&
                <div className="mb-4">
                    <label htmlFor="nomineePhoneWork" className="block font-medium mb-2">Work Phone</label>
                    <input id="nomineePhoneWork" type="tel"
                        className="max-w-none mb-1"
                        required={config.isPhoneWorkRequired}
                        onChange={handleChange}
                        value={nomination.nomineePhoneWork ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Work phone is required.</span>
                </div>
            }

            {config.allowPhoneMobile &&
                <div className="mb-4">
                    <label htmlFor="nomineePhoneMobile" className="block font-medium mb-2">Mobile Phone</label>
                    <input id="nomineePhoneMobile" type="tel"
                        className="max-w-none mb-1"
                        required={config.isPhoneMobileRequired}
                        onChange={handleChange}
                        value={nomination.nomineePhoneMobile ?? ''}
                        readOnly={readOnly}
                    />
                    <span className="field-error">Mobile phone is required.</span>
                </div>
            }

            {config.allowCustom1 &&
                <div className="mb-4">
                    <label htmlFor="custom1" className="block font-medium mb-2">{config.custom1Label}</label>
                    <textarea id="custom1"
                        className="max-w-none mb-1"
                        required={config.isCustom1Required}
                        onChange={handleTextAreaChange}
                        value={nomination.custom1 ?? ''}
                        readOnly={readOnly}
                        rows={3}
                    />
                    <span className="field-error">{config.custom1Label} is required.</span>
                </div>
            }

            {config.allowCustom2 &&
                <div className="mb-4">
                    <label htmlFor="custom2" className="block font-medium mb-2">{config.custom2Label}</label>
                    <textarea id="custom2"
                        className="max-w-none mb-1"
                        required={config.isCustom2Required}
                        onChange={handleTextAreaChange}
                        value={nomination.custom2 ?? ''}
                        readOnly={readOnly}
                        rows={3}
                    />
                    <span className="field-error">{config.custom2Label} is required.</span>
                </div>
            }

            {config.allowPhoto &&
                <div className="mb-4">
                    <label htmlFor="nomineePhoto" className="block font-medium mb-2">Photo</label>
                    {getNominationImage()}
                    {((isPreview && !nomination.nominationId) || !readOnly) &&
                        <LocalizationProvider language="photoUploadMessages">
                            <Upload id="nomineePhoto"
                                batch={false}
                                multiple={false}
                                onStatusChange={handlePhotoStatusChange}
                                onAdd={handlePhotoAdd}
                                restrictions={{
                                    allowedExtensions: ['.jpg', '.jpeg', '.bmp', '.png'],
                                    maxFileSize: 10485760
                                }}
                            />
                        </LocalizationProvider>
                    }
                    {photoMissing && showPhotoMissingError && <span className="field-error block">Photo is required.</span>}
                </div>
            }

            {config.allowBiography &&
                <div className="mb-4">
                    <label htmlFor="nomineeBio" className="block font-medium mb-2">Biography</label>

                    {!readOnly &&
                        <>
                            <Editor
                                className="max-w-none"
                                defaultEditMode="div"
                                defaultContent={nomination.nomineeBio ?? ''}
                                tools={[
                                    [FontName, FontSize],
                                    [Bold, Italic, Underline, Strikethrough],
                                    [Subscript, Superscript],
                                    [ForeColor, BackColor],
                                    [UnorderedList, OrderedList],
                                    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                                    [Link, Unlink]
                                ]}
                                onChange={handleEditorChange}
                            />
                            <span className="field-error">Biography is required.</span>
                        </>
                    }
                    {readOnly && nomination?.nomineeBio &&
                        <div className="w-full block shadow-sm border-gray-300 sm:text-sm rounded border-2 py-[8px] px-[12px]">
                            <span className="block" dangerouslySetInnerHTML={{ __html: dompurify.sanitize(nomination.nomineeBio.replaceAll("<p></p>","<p><br/></p>")) }} />
                        </div>
                    }
                </div>
            }

            {onBack && <button className="btn btn-gray mr-2" onClick={onBack}>Back</button>}

            {!readOnly && !isPreview &&
                <LoadingButton className="btn"
                    onBtnClick={handleSubmit}
                    btnText={nomination.nominationId ? "Update Nomination" : "Submit Nomination"}
                    isLoading={isSubmitting}
                    isSubmit={true}
                    confirmText="Are you sure you want to submit your nomination?" />
            }

        </form>
    </>
}